/**
 * Utility for client-side image compression and conversion to WebP format.
 * Compressing before upload dramatically reduces network payload and storage,
 * and speeds up page load times across the application.
 */

export interface ImageCompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.0 to 1.0 (default 0.82)
  outputFilename?: string;
}

/**
 * Loads an image from a File, Blob, or Data URL into an HTMLImageElement.
 */
function loadImage(source: File | Blob | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    let objectUrl: string | null = null;
    if (typeof source === 'string') {
      img.src = source;
    } else {
      objectUrl = URL.createObjectURL(source);
      img.src = objectUrl;
    }

    img.onload = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = (err) => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for compression: ' + err));
    };
  });
}

/**
 * Calculates new dimensions preserving aspect ratio within max bounds.
 */
function calculateDimensions(
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = srcWidth;
  let height = srcHeight;

  if (width > maxWidth || height > maxHeight) {
    const ratio = Math.min(maxWidth / width, maxHeight / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  return { width, height };
}

/**
 * Compresses an image File or Blob and converts it to a WebP File.
 * Suitable for multipart FormData uploads.
 */
export async function compressImageToWebP(
  file: File | Blob,
  options: ImageCompressOptions = {}
): Promise<File> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    // SSR fallback: return original if already a File, or convert Blob to File
    if (file instanceof File) return file;
    return new File([file], options.outputFilename || 'image.webp', { type: 'image/webp' });
  }

  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.82,
    outputFilename,
  } = options;

  const originalName = file instanceof File ? file.name : 'image.png';
  const baseName = originalName.replace(/\.[^/.]+$/, '');
  const finalFilename = outputFilename || `${baseName}.webp`;

  const img = await loadImage(file);
  const { width, height } = calculateDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height, maxWidth, maxHeight);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    if (file instanceof File) return file;
    return new File([file], finalFilename, { type: file.type || 'image/webp' });
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          if (file instanceof File) resolve(file);
          else resolve(new File([file], finalFilename, { type: 'image/webp' }));
          return;
        }
        const compressedFile = new File([blob], finalFilename, {
          type: 'image/webp',
          lastModified: Date.now(),
        });
        resolve(compressedFile);
      },
      'image/webp',
      quality
    );
  });
}

/**
 * Compresses an image (File, Blob, or base64 data URL) and returns a WebP data URL.
 * Ideal for components storing base64 URLs (e.g. quiz admin, custom lists).
 */
export async function compressImageToWebPDataUrl(
  source: File | Blob | string,
  options: ImageCompressOptions = {}
): Promise<string> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return typeof source === 'string' ? source : '';
  }

  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.82,
  } = options;

  const img = await loadImage(source);
  const { width, height } = calculateDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height, maxWidth, maxHeight);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return typeof source === 'string' ? source : '';
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  return canvas.toDataURL('image/webp', quality);
}
